/**
 * FleetMapView - Map view of fleet sites
 * Refactored to use MapWrapper for crash isolation
 */
import React, { useState, useMemo, lazy, Suspense } from 'react';
import { MapPin, Car, Building2, Filter, Phone, Mail, CreditCard, AlertTriangle, Wrench, UserPlus, Eye, Clock, ExternalLink, User, Radio, MapPinned } from 'lucide-react';
import PhoneLink, { cleanPhoneForUri } from '../common/PhoneLink.jsx';
import { useQuery } from '@tanstack/react-query';
import api from '../../api.js';
import MapWrapper, { L } from './MapWrapper.jsx';
import { useNavigation } from '../../v3/context/NavigationContext.jsx';
import { useFormat } from '../../i18n/format.js';
import { useTranslation } from 'react-i18next';
import { isFlagOn } from '../../utils/dbFlags';

// Phase 5: Lazy-load GPS map components (only loaded when GPS mode active)
const GPSLiveMap = lazy(() => import('./GPSLiveMap.jsx'));

// Site type configurations — pas de libellé ici : résolu au rendu via sites.types.<code>
// (constante de module figée au chargement = piège n°1 i18n, voir I18N_CONVENTIONS.md)
const SITE_TYPE_CONFIG = {
  siege: { color: '#EF4444', icon: 'depot' },
  agence: { color: '#F97316', icon: 'depot' },
  depot: { color: '#3B82F6', icon: 'depot' },
  parking: { color: '#8B5CF6', icon: 'parking' },
  atelier: { color: '#F59E0B', icon: 'workshop' },
  station: { color: '#10B981', icon: 'fuel' },
  client: { color: '#EC4899', icon: 'depot' },
  chantier: { color: '#6366F1', icon: 'depot' },
  autre: { color: '#6B7280', icon: 'depot' },
};

// Get SVG path for icons
function getIconPath(iconType) {
  switch (iconType) {
    case 'depot':
      return '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline>';
    case 'parking':
      return '<circle cx="12" cy="12" r="10"></circle><path d="M9 17V7h4a3 3 0 0 1 0 6H9"></path>';
    case 'workshop':
      return '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>';
    case 'fuel':
      return '<path d="M3 22V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16"></path><path d="M15 22V10a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v12"></path>';
    default:
      return '<circle cx="12" cy="12" r="3"></circle><path d="M12 2v4"></path><path d="M12 18v4"></path>';
  }
}

// Custom icon factory for sites
const createSiteIcon = (color, iconType) => {
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        ${getIconPath(iconType)}
      </svg>
    </div>`,
    className: 'custom-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

// Custom icon factory for vehicles
const createVehicleIcon = (status) => {
  const color = status === 'active' ? '#3B82F6' : status === 'maintenance' ? '#F59E0B' : '#6B7280';
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: 28px; height: 28px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8c-.1.2-.1.4-.1.6v4.7c0 .6.4 1 1 1h2"></path>
        <circle cx="7" cy="17" r="2"></circle>
        <circle cx="17" cy="17" r="2"></circle>
      </svg>
    </div>`,
    className: 'custom-vehicle-marker',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

// Site popup content
function SitePopup({ site, vehicleCount }) {
  const { t } = useTranslation();
  const config = SITE_TYPE_CONFIG[site.site_type] || SITE_TYPE_CONFIG.autre;
  const typeCode = SITE_TYPE_CONFIG[site.site_type] ? site.site_type : 'autre';
  const typeLabel = t(`sites.types.${typeCode}`, { defaultValue: typeCode });

  return (
    <div className="min-w-[200px]">
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: config.color }}
        >
          <Building2 className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{site.site_name || site.name}</h3>
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${config.color}20`, color: config.color }}
          >
            {typeLabel}
          </span>
        </div>
      </div>
      {site.address && (
        <p className="text-sm text-gray-600 mb-2">{site.address}</p>
      )}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1">
          <Car className="w-4 h-4 text-blue-500" />
          <span>{t('map.popup.vehicleCount', { count: vehicleCount })}</span>
        </div>
        {site.parking_capacity && (
          <div className="text-gray-500">
            {t('map.popup.capacity', { value: site.parking_capacity })}
          </div>
        )}
      </div>
    </div>
  );
}

// Status config — libellé résolu au rendu via vehicles.status.<code> (t(), pas de label figé ici)
const STATUS_CONFIG = {
  active: { color: '#10B981', bg: 'bg-green-100 text-green-800', gradient: 'from-blue-500 to-blue-600' },
  maintenance: { color: '#F59E0B', bg: 'bg-amber-100 text-amber-800', gradient: 'from-amber-500 to-amber-600' },
  retired: { color: '#6B7280', bg: 'bg-gray-100 text-gray-800', gradient: 'from-gray-500 to-gray-600' },
  ordered: { color: '#8B5CF6', bg: 'bg-violet-100 text-violet-800', gradient: 'from-violet-500 to-violet-600' },
  immobilized: { color: '#EF4444', bg: 'bg-red-100 text-red-800', gradient: 'from-red-500 to-red-600' },
};

// Driver initials from name
function getInitials(first, last) {
  return `${(first || '?')[0]}${(last || '?')[0]}`.toUpperCase();
}

// Enriched vehicle popup with driver ID card
const STATUS_FALLBACK_FR = {
  active: 'Actif',
  maintenance: 'En maintenance',
  retired: 'Retiré',
  ordered: 'Commandé',
  immobilized: 'Immobilisé',
};

function VehiclePopup({ vehicle, site, driver, onViewVehicle, onViewDriver }) {
  const { t, fmt } = useFormat();
  const status = STATUS_CONFIG[vehicle.status] || STATUS_CONFIG.active;
  const statusCode = STATUS_CONFIG[vehicle.status] ? vehicle.status : 'active';
  const statusLabel = t(`vehicles.status.${statusCode}`, { defaultValue: STATUS_FALLBACK_FR[statusCode] || statusCode });

  return (
    <div className="w-[310px] -m-3 overflow-hidden rounded-lg">
      {/* Vehicle header */}
      <div className={`bg-gradient-to-r ${status.gradient} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
            <Car className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-sm leading-tight">{vehicle.make} {vehicle.model}</div>
            <div className="text-white/70 text-xs">{vehicle.license_plate}</div>
          </div>
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.bg}`}>
          {statusLabel}
        </span>
      </div>

      {/* Vehicle stats row */}
      <div className="px-4 py-2.5 border-b border-gray-100 grid grid-cols-3 gap-1 text-center bg-white">
        <div>
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">{t('map.popup.km')}</div>
          <div className="text-xs font-bold text-gray-800">{vehicle.mileage ? vehicle.mileage.toLocaleString() : '—'}</div>
        </div>
        <div>
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">{t('map.popup.fuel')}</div>
          <div className="text-xs font-bold text-gray-800 truncate">{vehicle.fuel_type || '—'}</div>
        </div>
        <div>
          <div className="text-[9px] text-gray-400 uppercase tracking-wider">{t('map.popup.site')}</div>
          <div className="text-xs font-bold text-gray-800 truncate">{site?.site_name || '—'}</div>
        </div>
      </div>

      {/* Driver ID Card */}
      <div className="px-4 py-3 bg-white">
        <div className="text-[9px] text-gray-400 uppercase tracking-wider mb-2 font-semibold">{t('map.popup.driver')}</div>
        {driver ? (
          <div className="bg-gray-50 rounded-lg p-2.5 flex items-start gap-2.5 border border-gray-100">
            {/* Photo / Initials */}
            <div className="flex-shrink-0">
              {driver.photo_url ? (
                <img
                  src={driver.photo_url}
                  alt={`${driver.first_name} ${driver.last_name}`}
                  className="w-12 h-12 rounded-full object-cover"
                  style={{ boxShadow: '0 0 0 2px white, 0 0 0 4px #3B82F6' }}
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center"
                  style={{ boxShadow: '0 0 0 2px white, 0 0 0 4px #3B82F6' }}
                >
                  <span className="text-white font-bold text-sm">{getInitials(driver.first_name, driver.last_name)}</span>
                </div>
              )}
            </div>
            {/* Driver info */}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-900 text-sm leading-tight">
                {driver.first_name} {driver.last_name}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">DRV-{String(driver.id).padStart(6, '0')}</div>

              {/* Badges */}
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {(() => {
                  const cats = ['B','C','D','CE'].filter(c => driver[`license_category_${c.toLowerCase()}`]);
                  return cats.length > 0 ? (
                    <span className="inline-flex items-center gap-0.5 text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                      <CreditCard className="w-2.5 h-2.5" />
                      {t('map.popup.license', { categories: cats.join(', ') })}
                    </span>
                  ) : null;
                })()}
                {driver.license_expiry && (
                  <span className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                    new Date(driver.license_expiry) < new Date()
                      ? 'bg-red-50 text-red-700'
                      : new Date(driver.license_expiry) < new Date(Date.now() + 90 * 86400000)
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-green-50 text-green-700'
                  }`}>
                    <Clock className="w-2.5 h-2.5" />
                    {new Date(driver.license_expiry) < new Date()
                      ? t('map.popup.licenseExpired')
                      : t('map.popup.licenseExpiresOn', { date: fmt.date(driver.license_expiry, { month: '2-digit', year: 'numeric' }) })}
                  </span>
                )}
              </div>

              {/* Contact */}
              <div className="flex items-center gap-3 mt-1.5">
                {driver.phone && (
                  <a
                    href={`tel:${cleanPhoneForUri(driver.phone)}`}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={t('map.popup.callAriaLabel', { phone: driver.phone })}
                    className="flex items-center gap-0.5 text-[9px] text-gray-400 hover:text-blue-600 hover:underline"
                  >
                    <Phone className="w-2.5 h-2.5" />
                    {driver.phone}
                  </a>
                )}
                {driver.email && (
                  <span className="flex items-center gap-0.5 text-[9px] text-gray-400 truncate">
                    <Mail className="w-2.5 h-2.5 flex-shrink-0" />
                    {driver.email}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-3 border border-dashed border-gray-200 text-center">
            <div className="w-10 h-10 rounded-full bg-gray-200 mx-auto mb-1.5 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-gray-400" />
            </div>
            <div className="text-xs font-medium text-gray-500">{t('map.popup.noDriver')}</div>
          </div>
        )}
      </div>

      {/* Alerts row (CT, insurance) */}
      {(vehicle.ct_expiry_date || vehicle.status === 'maintenance') && (
        <div className="px-4 pb-2.5 bg-white">
          <div className="flex gap-1.5 flex-wrap">
            {vehicle.ct_expiry_date && new Date(vehicle.ct_expiry_date) < new Date() && (
              <span className="text-[9px] bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5" />
                {t('map.popup.ctExpired')}
              </span>
            )}
            {vehicle.ct_expiry_date && new Date(vehicle.ct_expiry_date) >= new Date() && new Date(vehicle.ct_expiry_date) < new Date(Date.now() + 60 * 86400000) && (
              <span className="text-[9px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5" />
                {t('map.popup.ctDueSoon')}
              </span>
            )}
            {vehicle.status === 'maintenance' && (
              <span className="text-[9px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                <Wrench className="w-2.5 h-2.5" />
                {t('map.popup.inWorkshop')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Action footer */}
      <div className="border-t border-gray-100 px-4 py-2.5 flex items-center justify-between bg-gray-50">
        <button
          onClick={() => onViewVehicle?.(vehicle)}
          className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 cursor-pointer"
        >
          <Eye className="w-3 h-3" />
          {t('map.popup.vehicleSheet')}
        </button>
        {driver ? (
          <button
            onClick={() => onViewDriver?.(driver)}
            className="text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1 cursor-pointer"
          >
            <User className="w-3 h-3" />
            {t('map.popup.driverSheet')}
          </button>
        ) : (
          <button
            onClick={() => onViewVehicle?.(vehicle)}
            className="text-[11px] text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1 cursor-pointer"
          >
            <UserPlus className="w-3 h-3" />
            {t('map.popup.assign')}
          </button>
        )}
      </div>
    </div>
  );
}

// Main FleetMapView component
function FleetMapView({ onMessage }) {
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(Object.keys(SITE_TYPE_CONFIG));
  const [mapMode, setMapMode] = useState('sites'); // 'sites' or 'gps'

  // Navigation handlers for popup actions
  const handleViewVehicle = (vehicle) => {
    navigateTo('vehicles', { selectedId: vehicle.id });
  };
  const handleViewDriver = (driver) => {
    navigateTo('drivers', { selectedId: driver.id });
  };

  // Fetch working sites
  const { data: sitesResult, refetch: refetchSites, isLoading: sitesLoading } = useQuery({
    queryKey: ['workingSites'],
    queryFn: () => api.workingSite.getAll(),
    staleTime: 60000,
  });
  const sitesExtracted = Array.isArray(sitesResult) ? sitesResult : (sitesResult?.data || []);
  const sites = Array.isArray(sitesExtracted) ? sitesExtracted : [];

  // Fetch vehicles for counts
  const { data: vehiclesResult } = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.vehicle.getAll(),
    staleTime: 60000,
  });
  const vehiclesExtracted = Array.isArray(vehiclesResult) ? vehiclesResult : (vehiclesResult?.data || []);
  const vehicles = Array.isArray(vehiclesExtracted) ? vehiclesExtracted : [];

  // Fetch drivers for popup enrichment
  const { data: driversResult } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => api.driver.getAll(),
    staleTime: 60000,
  });
  const driversExtracted = Array.isArray(driversResult) ? driversResult : (driversResult?.data || []);
  const drivers = Array.isArray(driversExtracted) ? driversExtracted : [];

  // Driver lookup by ID
  const driverMap = useMemo(() => {
    const map = {};
    drivers.forEach(d => { map[d.id] = d; });
    return map;
  }, [drivers]);

  // Fetch vehicle locations for site assignments
  const { data: locationsResult } = useQuery({
    queryKey: ['vehicleLocations'],
    queryFn: () => api.vehicleLocation.getAll(),
    staleTime: 60000,
  });
  const locationsExtracted = Array.isArray(locationsResult) ? locationsResult : (locationsResult?.data || []);
  const vehicleLocations = Array.isArray(locationsExtracted) ? locationsExtracted : [];

  // Calculate vehicle counts per site (from both working_site_id AND vehicle_locations)
  const vehicleCountBySite = useMemo(() => {
    const counts = {};
    const vehicleAssigned = new Set(); // Track which vehicles are already counted

    // First: count from vehicle_locations (current assignments)
    vehicleLocations.forEach(loc => {
      if (isFlagOn(loc.is_current) && loc.site_id) {
        counts[loc.site_id] = (counts[loc.site_id] || 0) + 1;
        vehicleAssigned.add(loc.vehicle_id);
      }
    });

    // Second: count from vehicle.working_site_id (if not already counted)
    vehicles.forEach(v => {
      if (v.working_site_id && !vehicleAssigned.has(v.id)) {
        counts[v.working_site_id] = (counts[v.working_site_id] || 0) + 1;
      }
    });

    return counts;
  }, [vehicles, vehicleLocations]);

  // Filter sites by selected types
  // Map unknown site_types (e.g. "headquarters") to "autre" for filtering
  const filteredSites = useMemo(() => {
    const knownTypes = Object.keys(SITE_TYPE_CONFIG);
    return sites.filter(site => {
      const type = knownTypes.includes(site.site_type) ? site.site_type : 'autre';
      return selectedTypes.includes(type);
    });
  }, [sites, selectedTypes]);

  // Build site-to-GPS lookup map
  const siteGPSMap = useMemo(() => {
    const map = {};
    sites.forEach(site => {
      const lat = parseFloat(site.gps_latitude);
      const lng = parseFloat(site.gps_longitude);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        map[site.id] = { lat, lng, site };
      }
    });
    return map;
  }, [sites]);

  // Build vehicle-to-site assignment map
  const vehicleSiteMap = useMemo(() => {
    const map = {};
    // From vehicle_locations (priority)
    vehicleLocations.forEach(loc => {
      if (isFlagOn(loc.is_current) && loc.site_id) {
        map[loc.vehicle_id] = loc.site_id;
      }
    });
    // From working_site_id (fallback)
    vehicles.forEach(v => {
      if (v.working_site_id && !map[v.id]) {
        map[v.id] = v.working_site_id;
      }
    });
    return map;
  }, [vehicles, vehicleLocations]);

  // Compute orbital positions (concentric rings) for N vehicles around a center point
  // Returns array of { latOffset, lngOffset } in degrees
  function computeOrbitalOffsets(count, baseLat) {
    if (typeof baseLat !== 'number' || isNaN(baseLat)) {
      return Array(count).fill({ latOffset: 0, lngOffset: 0 });
    }
    const offsets = [];
    let placed = 0;
    let ring = 0;
    // Base radius in degrees (~15m at equator; tight cluster around site)
    const latScale = 0.00012;
    const lngScale = latScale / Math.cos((baseLat * Math.PI) / 180); // correct for latitude

    while (placed < count) {
      ring++;
      const ringCapacity = 2 + ring * 4; // ring1=6, ring2=10, ring3=14, ...
      const radiusMult = 0.7 + ring * 0.6;
      const itemsInRing = Math.min(ringCapacity, count - placed);
      const angleStep = (2 * Math.PI) / itemsInRing;
      const angleOffset = ring % 2 === 0 ? angleStep / 2 : 0;

      for (let i = 0; i < itemsInRing; i++) {
        const angle = angleOffset + i * angleStep - Math.PI / 2;
        offsets.push({
          latOffset: Math.sin(angle) * latScale * radiusMult,
          lngOffset: Math.cos(angle) * lngScale * radiusMult,
        });
        placed++;
      }
    }
    return offsets;
  }

  // Build markers for MapWrapper (both sites AND vehicles in orbital layout)
  const markers = useMemo(() => {
    const allMarkers = [];

    // Add site markers (only sites with valid GPS coordinates)
    filteredSites.forEach(site => {
      const lat = parseFloat(site.gps_latitude);
      const lng = parseFloat(site.gps_longitude);
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
      const config = SITE_TYPE_CONFIG[site.site_type] || SITE_TYPE_CONFIG.autre;
      allMarkers.push({
        id: `site-${site.id}`,
        lat,
        lng,
        icon: createSiteIcon(config.color, config.icon),
        popup: <SitePopup site={site} vehicleCount={vehicleCountBySite[site.id] || 0} />
      });
    });

    // Group vehicles by site for orbital positioning
    const vehiclesBySite = {};
    vehicles.forEach(vehicle => {
      const siteId = vehicleSiteMap[vehicle.id];
      if (siteId && siteGPSMap[siteId]) {
        if (!vehiclesBySite[siteId]) vehiclesBySite[siteId] = [];
        vehiclesBySite[siteId].push(vehicle);
      }
    });

    // Add vehicle markers in orbital layout around each site
    Object.entries(vehiclesBySite).forEach(([siteId, siteVehicles]) => {
      const siteGPS = siteGPSMap[siteId];
      if (!siteGPS) return; // Skip vehicles on sites without GPS
      const { lat, lng, site } = siteGPS;
      const offsets = computeOrbitalOffsets(siteVehicles.length, lat);

      siteVehicles.forEach((vehicle, idx) => {
        const { latOffset, lngOffset } = offsets[idx] || { latOffset: 0, lngOffset: 0 };
        allMarkers.push({
          id: `vehicle-${vehicle.id}`,
          lat: lat + latOffset,
          lng: lng + lngOffset,
          icon: createVehicleIcon(vehicle.status),
          popup: <VehiclePopup vehicle={vehicle} site={site} driver={driverMap[vehicle.assigned_driver_id] || null} onViewVehicle={handleViewVehicle} onViewDriver={handleViewDriver} />
        });
      });
    });

    return allMarkers;
  }, [filteredSites, vehicles, vehicleCountBySite, vehicleSiteMap, siteGPSMap, driverMap]);

  // Stats
  const stats = useMemo(() => {
    const sitesWithGPS = filteredSites.filter(s => {
      const lat = parseFloat(s.gps_latitude);
      const lng = parseFloat(s.gps_longitude);
      return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    });
    return {
      totalSites: sitesWithGPS.length,
      totalVehicles: vehicles.filter(v => v.status !== 'retired').length,
      sitesByType: Object.entries(SITE_TYPE_CONFIG).map(([type, config]) => {
        const knownTypes = Object.keys(SITE_TYPE_CONFIG);
        return {
          type,
          ...config,
          count: filteredSites.filter(s => {
            const sType = knownTypes.includes(s.site_type) ? s.site_type : 'autre';
            return sType === type;
          }).length
        };
      })
    };
  }, [vehicles, filteredSites]);

  const toggleTypeFilter = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold">{t('map.header.title')}</h2>
              <p className="text-sm text-gray-500">
                {t('map.header.summary', { sites: stats.totalSites, vehicles: stats.totalVehicles })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setMapMode('sites')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  mapMode === 'sites' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MapPinned className="w-4 h-4" />
                {t('map.header.modeSites')}
              </button>
              <button
                onClick={() => setMapMode('gps')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  mapMode === 'gps' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Radio className="w-4 h-4" />
                {t('map.header.modeGpsLive')}
              </button>
            </div>

            {mapMode === 'sites' && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                {t('map.header.filters')}
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-medium text-gray-700 mb-2">{t('map.filters.siteTypesLabel')}</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(SITE_TYPE_CONFIG).map(([type, config]) => (
                <button
                  key={type}
                  onClick={() => toggleTypeFilter(type)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    selectedTypes.includes(type)
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                  style={{
                    backgroundColor: selectedTypes.includes(type) ? config.color : undefined
                  }}
                >
                  {t(`sites.types.${type}`, { defaultValue: type })}
                  <span className="text-xs opacity-75">
                    ({stats.sitesByType.find(s => s.type === type)?.count || 0})
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Map Content — Sites or GPS Live */}
      {mapMode === 'sites' ? (
        <>
          <div className="flex-1">
            <MapWrapper
              mapKey="fleet-map"
              markers={markers}
              onRefresh={refetchSites}
              isRefreshing={sitesLoading}
              height="100%"
              emptyMessage={t('map.empty.title')}
              emptyDescription={t('map.empty.description')}
              className="h-full"
            />
          </div>

          {/* Legend */}
          <div className="bg-white rounded-lg shadow-md p-3 mt-4">
            <div className="flex items-center gap-6 flex-wrap">
              <span className="text-sm font-medium text-gray-700">{t('map.legend.title')}</span>
              {Object.entries(SITE_TYPE_CONFIG).map(([type, config]) => (
                <div key={type} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: config.color }}
                  />
                  <span className="text-sm text-gray-600">{t(`sites.types.${type}`, { defaultValue: type })}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <Suspense fallback={
          <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <Radio className="w-8 h-8 text-green-500 animate-pulse mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t('map.gpsLive.connecting')}</p>
            </div>
          </div>
        }>
          <GPSLiveMap />
        </Suspense>
      )}
    </div>
  );
}

export default FleetMapView;
